import React, { useRef } from "react";
import { Group, Rect, Text } from "react-konva";
import Konva from "konva";
import { MondayLogoKonva } from "../header/logo";
import { ErrorIconKonva } from "./error-icon-konva";
import { MondayErrorCodes } from "shared/integrations/monday-types";
import {
  ARCHIVED_SVG,
  DELETED_SVG,
  LOCKED_SVG,
  VIEWER_ONLY_SVG,
  ACCOUNT_MISMATCH_SVG,
  FAILED_TO_LOAD_SVG,
} from "./error-icons";

const LOGO_SIZE = 17;
const ERROR_ICON_WIDTH = 53;
const ERROR_ICON_HEIGHT = 53;

enum ErrorBehavior {
  VIEW = "VIEW",
  RETRY = "RETRY",
  LOG_IN = "LOG_IN",
}

const ERROR_CONFIGS: Record<
  MondayErrorCodes,
  { icon: string; text: string; errorText?: string; behavior?: ErrorBehavior }
> = {
  [MondayErrorCodes.NOT_FOUND]: {
    icon: LOCKED_SVG,
    text: "No access to this item",
  },
  [MondayErrorCodes.BOARD_ARCHIVED]: {
    icon: ARCHIVED_SVG,
    text: "This item's board has been archived",
    errorText: "View board",
    behavior: ErrorBehavior.VIEW,
  },
  [MondayErrorCodes.BOARD_DELETED]: {
    icon: DELETED_SVG,
    text: "This item's board has been deleted",
    errorText: "View board",
    behavior: ErrorBehavior.VIEW,
  },
  [MondayErrorCodes.ARCHIVED]: {
    icon: ARCHIVED_SVG,
    text: "This item has been archived",
    errorText: "View item",
    behavior: ErrorBehavior.VIEW,
  },
  [MondayErrorCodes.DELETED]: {
    icon: DELETED_SVG,
    text: "This item has been deleted",
    errorText: "View item",
    behavior: ErrorBehavior.VIEW,
  },
  [MondayErrorCodes.ACCOUNT_MISMATCH]: {
    icon: ACCOUNT_MISMATCH_SVG,
    text: "This item is from another account",
  },
  [MondayErrorCodes.LOGIN_REQUIRED]: {
    icon: VIEWER_ONLY_SVG,
    text: "This item cannot be viewed by guests",
    errorText: "Log in",
    behavior: ErrorBehavior.LOG_IN,
  },
  [MondayErrorCodes.UNKNOWN_ERROR]: {
    icon: FAILED_TO_LOAD_SVG,
    text: "Unable to load item",
    errorText: "Retry",
    behavior: ErrorBehavior.RETRY,
  },
};

interface MondayTaskErrorProps {
  width: number;
  height: number;
  code?: MondayErrorCodes;
  url?: string;
}

export const MondayTaskError = React.memo(
  ({ width, height, code = MondayErrorCodes.NOT_FOUND, url }: MondayTaskErrorProps) => {
    const textRef = useRef<Konva.Text>(null);
    const centerX = width / 2;
    const startY = 16;
    const logoHeight = LOGO_SIZE * 0.6;

    const { icon, text, errorText, behavior } = ERROR_CONFIGS[code];

    const handleActionClick = () => {
      switch (behavior) {
        case ErrorBehavior.VIEW: {
          if (url) {
            window.open(url, "_blank");
          }
          break;
        }
        case ErrorBehavior.RETRY: {
          window.location.reload();
          break;
        }
        case ErrorBehavior.LOG_IN: {
          window.location.href = "https://app.workcanvas.com/";
          break;
        }
        default: {
          break;
        }
      }
    };

    const handleMouseEnter = () => {
      const stage = textRef.current?.getStage();
      if (stage) {
        stage.container().style.cursor = "pointer";
      }
    };

    const handleMouseLeave = () => {
      const stage = textRef.current?.getStage();
      if (stage) {
        stage.container().style.cursor = "default";
      }
    };

    return (
      <Group shadowColor="black" shadowBlur={14} shadowOpacity={0.1} shadowForStrokeEnabled={false}>
        <Rect
          width={width}
          height={height}
          fill="white"
          cornerRadius={10}
          stroke="#EBEDF3"
          strokeWidth={1}
          hitStrokeWidth={0}
        />

        <MondayLogoKonva x={16} y={startY} width={LOGO_SIZE} height={logoHeight} />

        <ErrorIconKonva
          x={centerX - ERROR_ICON_WIDTH / 2}
          y={startY + logoHeight}
          width={ERROR_ICON_WIDTH}
          height={ERROR_ICON_HEIGHT}
          icon={icon}
        />

        <Text
          y={startY + logoHeight + ERROR_ICON_HEIGHT - 12}
          width={width}
          text={text}
          fontFamily="Poppins"
          fontSize={12}
          fill="#1E1E1E"
          align="center"
          lineHeight={1.3}
          letterSpacing={0.187}
        />

        {errorText && (
          <Text
            ref={textRef}
            x={centerX - 100 / 2}
            y={startY + logoHeight + ERROR_ICON_HEIGHT + 16}
            width={100}
            text={errorText}
            fontFamily="Poppins"
            fontSize={12}
            fill="#1973FF"
            align="center"
            onClick={handleActionClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </Group>
    );
  }
);

MondayTaskError.displayName = "MondayTaskError";
